import { BrowserRouter, Routes, Route } from 'react-router-dom';
import * as routes from './utils/routes';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import React, { useEffect } from 'react';
import Navigation from './components/Navigation';
import Register from './pages/Register';
import ForgottenPassword from './pages/ForgottenPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Variants from './pages/Products';
import Orders from './pages/Orders';
import Inventory from './pages/Inventory';
import Assets from './pages/Assets';
import Expenses from './pages/Expenses';
import ActivityLogs from './pages/ActivityLogs';
import Customers from './pages/Customers';
import CustomerGroupReports from './pages/CustomerGroupReports';
import Subscription from './pages/Subscription';
import SubscriptionCallback from './pages/SubscriptionCallback';
import PrivateRoutes from './components/PrivateRoutes';
import CustomSnackbar from './components/CustomSnackbar';
import { SnackbarProvider } from 'notistack';
import Notifier from './components/Notifier';
import NotFound from './pages/NotFound';
import { ThemeProvider } from 'styled-components';
import { createTheme } from '@mui/material';
import ReactGA from 'react-ga4';
import { LandingPage } from './pages/LandingPage';
import ScrollToTop from './components/ScrollToTop';
import 'react-datepicker/dist/react-datepicker.css';
import ShopSelector from './pages/ShopSelector';
import UserManagement from './pages/UserManagement';
import ShopSelectorRoute from './components/ShopSelectorRoute';
import Account from './pages/Account';
import StorefrontRoute from './components/StorefrontRoute';
import { isSubdomain } from './utils/subdomainHelper';
import InvoicePage from './pages/InvoicePage';
import { useSubscription } from './hooks/useSubscription';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
	useEffect(() => {
		ReactGA.initialize('G-K3FT80VCYG');
	}, []);

	// Remove this - subscription is already fetched in Dashboard and other components that need it
	// useSubscription();

	const theme = createTheme();

	// If we're on a subdomain, we need to render the storefront without the navigation
	if (isSubdomain()) {
		return (
			<ErrorBoundary>
				<BrowserRouter>
					<SnackbarProvider
						Components={{
							default: CustomSnackbar,
							error: CustomSnackbar,
							success: CustomSnackbar,
							warning: CustomSnackbar,
							info: CustomSnackbar,
						}}
					>
						<ThemeProvider theme={theme}>
							<Notifier />
							<Routes>
								{/* All subdomain paths go to StorefrontRoute which will handle shop not found cases */}
								<Route path="*" element={<StorefrontRoute />} />
							</Routes>
						</ThemeProvider>
					</SnackbarProvider>
				</BrowserRouter>
			</ErrorBoundary>
		);
	}

	// Otherwise, render the main app
	return (
		<ErrorBoundary>
			<BrowserRouter>
				<SnackbarProvider
					Components={{
						default: CustomSnackbar,
						error: CustomSnackbar,
						success: CustomSnackbar,
						warning: CustomSnackbar,
						info: CustomSnackbar,
					}}
				>
					<>
						<ThemeProvider theme={theme}>
							<Notifier />
							<Navigation>
								<ScrollToTop />
								<Routes>
									<Route element={<PrivateRoutes />}>
										<Route path={routes.DASHBOARD} element={<Dashboard />} />
										<Route path={routes.PRODUCTS} element={<Variants />} />
										<Route path={routes.EXPENSES} element={<Expenses />} />
										<Route path={routes.ORDERS} element={<Orders />} />
										<Route path={routes.INVENTORY} element={<Inventory />} />
										<Route path={routes.ASSETS} element={<Assets />} />
										<Route path={routes.USER_MANAGEMENT} element={<UserManagement />} />
										<Route path={routes.ACTIVITY_LOGS} element={<ActivityLogs />} />
										<Route path={routes.ACCOUNT} element={<Account />} />
										<Route path={routes.CUSTOMERS} element={<Customers />} />
										<Route path={routes.CUSTOMERS_ALL} element={<Customers />} />
										<Route path={routes.CUSTOMER_GROUPS} element={<Customers />} />
										<Route
											path={routes.CUSTOMER_GROUP_REPORTS}
											element={<CustomerGroupReports />}
										/>
										<Route path={routes.SUBSCRIPTION} element={<Subscription />} />
										<Route path={routes.SUBSCRIPTION_CALLBACK} element={<SubscriptionCallback />} />
									</Route>
									<Route element={<ShopSelectorRoute />}>
										<Route path={routes.SHOP_SELECTOR} element={<ShopSelector />} />
									</Route>
									<Route path={routes.LANDING_PAGE} element={<LandingPage />} />
									<Route path={routes.LOGIN} element={<Login />} />
									<Route path={routes.REGISTER} element={<Register />} />
									<Route path={routes.FORGOTTEN_PASSWORD} element={<ForgottenPassword />} />
									<Route path={routes.RESET_PASSWORD} element={<ResetPassword />} />
									<Route path={routes.VERIFY_EMAIL} element={<VerifyEmail />} />
									<Route path={routes.INVOICE} element={<InvoicePage />} />
									<Route path={routes.NOT_FOUND} element={<NotFound />} />
									<Route path="*" element={<NotFound />} />
								</Routes>
							</Navigation>
						</ThemeProvider>
					</>
				</SnackbarProvider>
			</BrowserRouter>
		</ErrorBoundary>
	);
}

export default App;
